/**
 * Verzeichnis der Vorlagen und Vorschläge aus dem Schritttext.
 *
 * Neue Vorlagen müssen nur in templates/index.ts stehen, dann erscheinen sie
 * im Editor, in der Werkbank, im Prüfskript und in den Tests.
 */
import { ALL_TEMPLATES } from './templates/index.ts';
import type { AnimationTemplate, ParameterValue, TemplateGroup } from './template.ts';
import { TEMPLATE_GROUPS } from './template.ts';

/** Alle vorhandenen Vorlagen. */
export const ANIMATION_TEMPLATES: readonly AnimationTemplate[] = ALL_TEMPLATES;

/** Sucht eine Vorlage anhand ihrer Kennung. */
export function findTemplate(id: string | null | undefined): AnimationTemplate | undefined {
  if (id === null || id === undefined || id === '') {
    return undefined;
  }
  return ANIMATION_TEMPLATES.find((template) => template.id === id);
}

/** Vorlagen nach Bereichen, in der Reihenfolge der Galerie. */
export function templatesByGroup(): {
  group: TemplateGroup;
  label: string;
  templates: AnimationTemplate[];
}[] {
  return (Object.keys(TEMPLATE_GROUPS) as TemplateGroup[]).map((group) => ({
    group,
    label: TEMPLATE_GROUPS[group],
    templates: ANIMATION_TEMPLATES.filter((template) => template.group === group),
  }));
}

/** Bringt Text in eine Vergleichsform: klein, ohne Satzzeichen. */
export function normalizeText(text: string): string {
  return ` ${text
    .toLowerCase()
    .replace(/ß/g, 'ss')
    .replace(/[^a-zäöü0-9]+/g, ' ')
    .trim()} `;
}

/** Ein Vorschlag mit Bewertung und aus dem Text gelesenen Werten. */
export interface Suggestion {
  readonly template: AnimationTemplate;
  readonly score: number;
  readonly values: Readonly<Record<string, ParameterValue>>;
}

/**
 * Sucht Vorlagen, die zu einem Schritttext passen.
 *
 * Stichwörter sind Wortanfänge: „würfel“ passt zu „würfeln“ und „gewürfelt“
 * nur, wenn beide als Stichwort stehen. Mehrwortige Stichwörter zählen mehr.
 * Das Ergebnis ist absteigend nach Bewertung sortiert.
 */
export function suggestTemplates(text: string, limit = 4): Suggestion[] {
  const haystack = normalizeText(text);
  if (haystack.trim() === '') {
    return [];
  }
  const results: Suggestion[] = [];
  for (const template of ANIMATION_TEMPLATES) {
    let score = 0;
    for (const keyword of template.keywords) {
      const needle = normalizeText(keyword).trimEnd();
      if (haystack.includes(needle)) {
        score += 1 + keyword.split(' ').length;
      }
    }
    if (score > 0) {
      const inferred = Object.entries(template.inferValues?.(text) ?? {}).filter(
        (entry): entry is [string, ParameterValue] => entry[1] !== undefined,
      );
      results.push({ template, score, values: Object.fromEntries(inferred) });
    }
  }
  return results.sort((a, b) => b.score - a.score).slice(0, limit);
}
